import { useConnectionStore, Toasts } from '@sentinel/web-core';
import { Icon } from '@sentinel/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountsFooter } from './AccountsFooter';
import { AccountsTopbar } from './AccountsTopbar';
import { SceneHud } from './scene/SceneHud';
import { TerritoryScene, type RecognisedOrganization } from './scene/TerritoryScene';

interface Props {
  /** Line under the greeting: it follows the step of the card. */
  subtitle: ReactNode;
  /** First name, once known. */
  name?: string | undefined;
  /** Organization recognised from the email domain: pinned on the territory. */
  organization?: RecognisedOrganization | null;
  children: ReactNode;
}

/** Every page outside the shell (sign-in, password recovery, invitation): the territory on the
 *  left, the desk on the right with one card framed by the corner brackets (rule 11). */
export function AccountsLayout({ subtitle, name, organization = null, children }: Props) {
  const { t } = useTranslation();
  const online = useConnectionStore((s) => s.browserOnline);
  return (
    <div className="acc">
      <TerritoryScene organization={organization} />
      <SceneHud name={name} subtitle={subtitle} />
      <main className="acc-side">
        <AccountsTopbar />
        <div className="acc-stage">
          {online ? null : (
            <div className="acc-banner" role="alert">
              <Icon name="offline" size={18} />
              <div>
                <b>{t('accounts.offline.title')}</b>
                {t('accounts.offline.detail')}
              </div>
            </div>
          )}
          <section className="acc-card" aria-live="polite">
            <span className="tk" />
            {children}
          </section>
          <AccountsFooter />
        </div>
      </main>
      <Toasts />
    </div>
  );
}
